import { v4 as uuidv4 } from 'uuid';

import { Product } from '@setvi/shared/interfaces';
import { valueToMonetary } from 'pages/products/utils';

import { ManualProps } from '../pages/details/components/manual-entry';
import { CompetitorProps, RfqMatchOverride, RFQProducts } from '../interfaces';

export const acceptedFileTypes: Record<string, string[]> = {
  'image/*': ['.jpeg', '.jpg', '.png'],
  'application/pdf': [],
  'application/vnd.ms-excel': [],
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': [],
  'text/csv': ['.csv']
};

export interface OtherMatchesProps {
  _id: string;
  reasoning: string;
  percentage: number;
  isPrivateLabel: boolean;
}

export const generateManualEntry = (props: {
  sku: string;
  name: string;
  price: number;
  manufacturer: string;
  unitOfMeasure?: string;
}) => ({
  _id: uuidv4(),
  sku: props.sku,
  vendor: {
    _id: uuidv4(),
    vendorId: uuidv4(),
    name: props?.manufacturer || ''
  },
  name: props.name,
  cost: props?.price || 0,
  unitOfMeasure: props?.unitOfMeasure || ''
});

export const cleanValueForCsv = (val: string) =>
  val?.toString()?.replace(/"/g, '""');

export const formatProduct = (product: any) => {
  let matchedProduct = product?.product;
  if (!matchedProduct && product?.name) {
    matchedProduct = {
      name: product?.name,
      sku: product?.sku,
      manufacturerSku: product?.manufacturerSku,

      vendor: {
        name: product?.manufacturer
      }
    };
  }

  const currMatched = product?.otherMatches?.find(
    (m: any) => m?._id === product?.productId || m?.id === product?.productId
  );

  return {
    internalIndex: 0,
    id: product?._id,
    cost: product?.cost,
    productId: product?.productId || '',
    productName: product?.competitor?.name,
    competitorSku: product?.competitor?.sku,
    fileUrl: product?.fileUrl,
    desc: product?.competitor?.description,
    manufacturer: product?.competitor?.manufacturer,
    manufacturerSku: product?.competitor?.manufacturerSku,
    percentage: product?.percentage || null,
    selected: product?.selected,
    quantity: product?.quantity ? Number(product?.quantity) : '',
    prevItemPercentage: product?.prevItemPercentage || null,
    hasPrivateLabels: product?.hasPrivateLabels || false,
    order: product?.order,
    matchOverride: product?.matchOverride || null,
    isMatched: product?.isMatched || null,
    inQuote: product?.inQuote || null,
    sell: product?.sell || null,
    matchedInternalProducts: matchedProduct
      ? [
          {
            ...matchedProduct,
            _id: currMatched?._id || product?.productId || null,
            cost: currMatched?.cost || product?.cost || 0,
            percentage: currMatched?.percentage || null,
            reasoning: currMatched?.reasoning || '',
            external: product?.external || false
          }
        ]
      : [],
    matchedInternalProductsId: product?.otherMatches,
    unitPrice: product?.competitor?.price,
    ignored: product?.ignored,
    mfgSkuMatched: product?.isMfgMatched,
    foundOnline: !!product?.foundOnline?.name || !!product?.foundOnline?.url,
    foundItem: {
      name: product?.foundOnline?.name,
      url: product?.foundOnline?.url,
      manufacturer: product?.foundOnline?.manufacturer,
      manufacturerSku: product?.foundOnline?.manufacturerSku,
      isManufacturerExistInDB: product?.foundOnline?.manufacturerExistsInDB
    },
    customAttributes: product?.competitor?.specifications,
    summary: {
      description: product?.competitor?.summary,
      specifications: product?.competitor?.attributes || []
    },
    manualEntry: product?.manualEntry || false,
    statusId: product?.statusId || '',
    attributeLineNo: product?.attributeLineNo,
    excelRow: product?.excelRow,
    originalOrder: product?.originalOrder,
    customerNote: product?.customerNote || '',
    internalNotesCount: product?.internalNotesCount || 0,
    lastModifiedBy: product?.lastModifiedBy || null,
    lastModifiedDate: product?.lastModifiedDate || null
  };
};

export const formatProductBody = ({
  competitor,
  selected,
  product,
  order,
  edit,
  fileUrl
}: {
  competitor: any;
  selected: boolean;
  product: any;
  order?: number;
  edit?: boolean;
  fileUrl: string;
}) => ({
  _id: competitor?._id || competitor?.id,
  ...(!!order && { order }),
  name: product?.name || '',
  description: product?.description || '',
  productId: product?._id || '',
  competitor: {
    manufacturerSku: competitor.manufacturerSku || '',
    specifications: competitor?.customAttributes?.length
      ? competitor?.customAttributes?.map(
          ({ label, value }: { label: string; value: string }) => ({
            label: label || '',
            value: value || ''
          })
        )
      : [],
    manufacturer: competitor?.manufacturer || '',
    description: competitor?.desc || '',
    attributes: competitor?.summary?.specifications || [],
    summary: competitor?.summary?.description || '',
    ...(competitor?.unitPrice && { price: competitor?.unitPrice }),
    name: competitor?.productName || '',
    sku: competitor?.competitorSku || '',
    ...(competitor?.quantity && {
      quantity: competitor?.quantity?.toString()
    })
  },
  fileUrl,
  hasPrivateLabels: competitor?.hasPrivateLabels || false,
  foundOnline: {
    manufacturerExistsInDB:
      competitor?.foundItem?.isManufacturerExistInDB || false,
    manufacturerSku: competitor?.foundItem?.manufacturerSku || '',
    manufacturer: competitor?.foundItem?.manufacturer || '',
    name: competitor?.foundItem?.name || '',
    url: competitor?.foundItem?.url || ''
  },
  ...(product?.quantity && { quantity: product?.quantity }),
  sku: product?.sku || '',
  ...(product?.price && { price: product?.price }),
  cost: product?.cost || 0,
  ignored: competitor?.ignored || false,
  selected,
  external: product?.external || false,
  reasoning: product?.reasoning || '',
  ...(competitor?.manualEntry ||
  competitor?.matchOverride === RfqMatchOverride.UserSelection
    ? { percentage: 0 }
    : (product?.percentage >= 0 || competitor?.percentage >= 0) && {
        percentage: product?.percentage || competitor?.percentage || 0
      }),
  isMfgMatched: competitor?.mfgSkuMatched || false,
  manufacturer: product?.vendor?.name || '',
  manufacturerSku: product?.manufacturerSku || '',
  otherMatches: !edit
    ? competitor?.matchedInternalProducts?.map(
        (
          { _id, reasoning, percentage, isPrivateLabel }: OtherMatchesProps,
          i: number
        ) => ({
          id: _id,
          ...(reasoning && { reasoning }),
          ...(percentage && { percentage }),
          isPrivateLabel: isPrivateLabel || false,
          order: i
        })
      ) || []
    : competitor?.matchedInternalProductsId?.map((other: any) => {
        const { _id, ...restOther } = other;
        return {
          ...restOther,
          id: _id || other?.id
        };
      }) || []
});

// Change existing match with manual entry or add new manual entry
export const getManualEntry = (
  products: RFQProducts[],
  props: ManualProps,
  productId: string
) => {
  const pricing = {
    price: Number(props.price),
    cost: Number(props.price),
    sell: Number(props.price)
  };

  const newProps = {
    ...props,
    ...pricing
  };

  const findIndex = products.findIndex(p => p?.id === productId);

  const element = products[findIndex];
  const noMatched = !element?.matchedInternalProducts;

  const newProduct = {
    ...generateManualEntry(newProps),
    external: true
  };

  const modifiedElement = {
    ...element,
    ...pricing,
    manualEntry: true,
    isMatched: true,
    percentage: 0,
    matchOverride: RfqMatchOverride.UserSelection,
    prevItemPercentage:
      element?.matchedInternalProducts?.[element?.internalIndex]?.percentage ||
      element?.prevItemPercentage,
    internalIndex: noMatched ? 0 : element?.matchedInternalProducts?.length,
    matchedInternalProducts: noMatched
      ? [newProduct]
      : [...element?.matchedInternalProducts, newProduct]
  };

  const newProducts = [
    ...products.slice(0, findIndex),
    modifiedElement as any,
    ...products.slice(findIndex + 1)
  ];

  let matchedIncreased = false;
  if (noMatched) {
    matchedIncreased = true;
  }

  return { id: productId, newProducts, matchedIncreased, modifiedElement };
};

export const getProductChange = (
  products: RFQProducts[],
  product: Product,
  productId: string,
  isExternal = true
) => {
  const findIndex = products.findIndex(p => p?.id === productId);
  const element = products[findIndex];

  const mfgSkuMatched =
    product?.manufacturerSku === element?.manufacturerSku || false;

  const suggestions = element?.matchedInternalProducts || [];

  const isMatched = suggestions?.find(s => s?._id === product?._id);
  const matchedInternalIndex = suggestions?.findIndex(
    s => s?._id === product?._id && s?.sku === product?.sku
  );

  if (!isMatched) {
    suggestions?.push({
      ...product,
      external: isExternal
    });
  }

  const internalIndex = isMatched
    ? matchedInternalIndex
    : (suggestions?.length || 1) - 1;

  const modifiedElement = {
    ...element,
    cost: product?.cost || element?.cost,
    manualEntry: false,
    internalIndex,
    mfgSkuMatched,
    matchedInternalProducts: suggestions,
    percentage: element?.matchedInternalProducts?.[internalIndex]?.percentage,
    prevItemPercentage:
      element?.percentage ||
      element?.matchedInternalProducts?.[internalIndex]?.percentage
  };

  const newProducts = [
    ...products.slice(0, findIndex),
    modifiedElement as any,
    ...products.slice(findIndex + 1)
  ];

  let matchedIncreased = false;
  if (!suggestions?.length) {
    matchedIncreased = true;
  }

  if (isExternal)
    return {
      id: productId,
      modifiedElement,
      newProducts
    };

  const bucket = (p: number) => {
    if (p == null) return null; // ignore undefined/null
    if (p === 100) return 'exactMatch';
    if (p >= 80) return 'closeMatch';
    return 'possibleMatch'; // note: 0–79 go here
  };

  const totalUpdates: any = {};

  const newBucket = bucket(modifiedElement?.percentage);
  const oldBucket = bucket(modifiedElement?.prevItemPercentage);

  // If change did not happen within matched do not update analytics
  if (!modifiedElement?.percentage)
    return {
      id: productId,
      modifiedElement,
      newProducts
    };

  if (oldBucket !== newBucket) {
    if (oldBucket) totalUpdates[oldBucket] = -1;
    if (newBucket) totalUpdates[newBucket] = 1;
  }

  const mfgSkuMatchChanged = element?.mfgSkuMatched !== mfgSkuMatched;
  return {
    id: productId,
    modifiedElement,
    newProducts,
    matchedIncreased,
    mfgSkuMatched,
    mfgSkuMatchChanged,
    ...totalUpdates
  };
};

export const formateCSV = ({
  competitor,
  product,
  isSingerUser = false
}: {
  competitor: CompetitorProps;
  product: Partial<Product>;
  isSingerUser?: boolean;
}) => {
  const cost = Number(product?.cost)
    ? valueToMonetary(product?.cost)
    : product?.cost || '';
  const competitorPrice = Number(competitor?.price)
    ? valueToMonetary(Number(competitor?.price))
    : competitor?.price || '';

  return {
    'Competitor Name': cleanValueForCsv(competitor?.name || ''),
    'Competitor Sku': cleanValueForCsv(competitor?.sku || ''),
    'Competitor Manufacturer': cleanValueForCsv(competitor?.manufacturer || ''),
    'Competitor Manufacturer SKU': cleanValueForCsv(
      competitor?.manufacturerSku || ''
    ),
    'Competitor Price': cleanValueForCsv(competitorPrice || ''),
    'Competitor Quantity': cleanValueForCsv(
      competitor?.quantity?.toString() || ''
    ),
    'Product Name': cleanValueForCsv(product?.name || ''),
    'Product SKU': cleanValueForCsv(product?.sku || ''),
    'Product Description': cleanValueForCsv(product?.description || ''),
    'Manufacturer Name': cleanValueForCsv(product?.vendor?.name || ''),
    'Manufacturer SKU': cleanValueForCsv(product?.manufacturerSku || ''),
    ...(isSingerUser && {
      'Product Cost': cleanValueForCsv(cost?.toString() || '')
    }),
    'Match Percentage': cleanValueForCsv(
      `${product?.percentage ? `${product?.percentage?.toString()}%` : ''}` ||
        ''
    ),
    'Match Reasoning': cleanValueForCsv(product?.reasoning || '')
  };
};

// idClassifier.ts

const SALESFORCE_ID_RE = /^[a-zA-Z0-9]{15}([a-zA-Z0-9]{3})?$/;
// UUID v1–v5 (case-insensitive)
const UUID_RE =
  /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[1-5][0-9a-fA-F]{3}-[89abAB][0-9a-fA-F]{3}-[0-9a-fA-F]{12}$/;

export const classifyId = (id: string): string => {
  const trimmed = (id ?? '').trim();

  if (SALESFORCE_ID_RE.test(trimmed)) {
    return 'catalog';
  }

  if (UUID_RE.test(trimmed)) {
    return 'manull';
  }

  return '';
};

/**
 * Parses old RFQ processing time string (e.g. "11:50 min") to seconds.
 * Returns 0 if the format is unrecognized or value is falsy.
 */
export const parseProcessingTimeToSeconds = (time?: string): number => {
  if (!time) return 0;

  const match = time.match(/^(\d+):(\d+)\s*(?:min|secs?)$/i);
  if (!match) return 0;

  const minutes = parseInt(match[1], 10);
  const seconds = parseInt(match[2], 10);

  return minutes * 60 + seconds;
};

export const getItemAnalyticsBucket = (p: number) => {
  if (p == null) return null; // ignore undefined/null
  if (p === 100) return 'exactMatch';
  if (p >= 80) return 'closeMatch';
  return 'possibleMatch'; // note: 0–79 go here
};
